import { resolveLearningContentKind } from "@/components/luagate/LearningActivityTag";

/*
学習パスの項目 1 本の種類。受講生が「開いたら何をするか」を一語で見分けるための札で、
地図で章を開いた一覧、一覧のタブ、項目のページの頭と学習目次が、どれもここを通す (画面ごとに決め方を分けない)。

決め方は本番の team_curriculums の列だけで、上から順に当てる。
  実践   題に「実践PJ」「実践プロジェクト」(実践プロジェクトの画面で進める項目)
  道場   道場のコース (題に「道場」、または dojo-* のコース)
  動画   item_type が lecture
  確認テスト  item_type が quiz
  手を動かす  クラウドエディターの手を動かす章が付き、見ている人がエディターに入れる (呼ぶ側が handsOn で渡す)
  やってみる  エディターの外で自分がやる項目 (投稿する、登録する、入れる、設定する、予約する)。題の推測でなく、下の TASK_PATH_ITEM_IDS の項目 id の表で当てる
  宿題   item_type が homework
  読む   それ以外 (記事、レッスン、コースの本文をブラウザで読む)

手を動かすは見る人で変わる。エディターに入れない人 (今は受講生と体験の人) が押すと本文が開くので、
その人には元の種類 (やってみる、宿題、読む) を出す。押した先と札を食い違わせない
*/

export type PathItemKind = "practice" | "dojo" | "video" | "quiz" | "hands-on" | "task" | "homework" | "reading";

/** 並べるときの順 (全体の進みの内訳など)。札の名は LgKindTag の LG_KINDS */
export const PATH_ITEM_KINDS: readonly PathItemKind[] = ["reading", "hands-on", "task", "homework", "dojo", "practice", "video", "quiz"];

/**
 * 「やってみる」の項目 (team_curriculums.id)。本番の既定の学習パスを 2026-10-16 に読み取りで見て、題と本文から
 * 「エディターの外で自分が行動する」と決めた 18 本。題の言い回しでは当てない (「のぞいてみよう」にも読むだけの回がある)。
 * 項目を足す、直すときはこの表に足す。item_type は task のまま
 */
export const TASK_PATH_ITEM_IDS: ReadonlySet<string> = new Set([
  "e9e2861c-81ca-4e74-857a-b33ca05c1f9f", // 自己紹介してみよう
  "0eb65f98-1731-4225-919d-d4e32ca03258", // 講師に質問してみよう
  "71c5e5ae-a675-433c-82d1-86fb9cb26746", // Zoom日程を確認しよう
  "e0ba1e0d-fdf4-466b-bedb-822aa92ae267", // オンボーディング日程を確認しよう
  "6b46e5a1-7be5-4590-9b34-413c2579921e", // 過去動画をのぞいてみよう
  "ecfb56c8-6101-471c-8c3a-7ce9ddf0aedf", // 資料保管庫をのぞいてみよう
  "180ce6d7-f52e-4d42-a161-00213902fe78", // 限定コラムをのぞいてみよう
  "be299ccd-815a-49ee-9541-941309976135", // 学習用 X アカウントを作ろう
  "a49833f9-6525-4bd1-96fe-4777df546069", // 案件募集をのぞいてみよう
  "9f53b744-0ae8-4abf-9b0f-2ce05074557c", // 今日/今週の日報を書いてみよう
  "35744e76-d441-4150-9bb2-ad0901f2c28f", // タイピング練習をしてみよう
  "69304e24-1c38-47ff-a2e6-31b3ae2899a8", // バーチャルスペース「Teracy」の導入
  "65a30bc7-e930-4f9f-b33c-fdd5e6b9fd54", // 講師と面談しましょう！
  "9a56c43c-0db3-440c-b7e9-150aea9fa173", // Mattermostに入ろう（コミュニティ）
  "21f9db26-83da-471b-9337-492f568b4261", // Gitのインストールと初期設定
  "89dbb6f9-66ee-49df-9376-dec0c5474e54", // GitHubアカウント作成とリポジトリ操作
  "c632cfcc-a0e5-46b4-99ec-03b8e33b540a", // Python環境構築（pip / venv）
  "8c3ba2cd-0572-437f-9ba7-62aa0ce68c83", // Java開発環境のセットアップ
  // 実プロダクト開発演習パス (v2、10/19) の手を動かす 3 本。エディターの外の手元でもやるので「やってみる」
  "26e547af-e0e7-a719-bb14-ed8bedd09e3a", // 1 つの操作をコードでたどろう
  "35dc047e-70ed-083f-3875-b7e4fe29f514", // 開発の場所を用意して、自分の枝を切ろう
  "5ba70246-cc8c-ada3-497b-362dec8fc4e3", // 画面の文言を 1 か所直して、手元で確かめよう
]);

export type PathItemKindInput = {
  /** team_curriculums.id。「やってみる」の表に当てる */
  id?: string | null;
  itemType: string;
  title?: string;
  /** 道場のコースの slug (関連教材から決まる)。無ければ題だけで見る */
  courseSlug?: string | null;
};

export function pathItemKind({ id, itemType, title = "", courseSlug }: PathItemKindInput, handsOn = false): PathItemKind {
  const content = resolveLearningContentKind(itemType, title, courseSlug);
  if (content === "実践PJ") return "practice";
  if (content === "道場") return "dojo";
  if (itemType === "lecture") return "video";
  if (itemType === "quiz") return "quiz";
  if (handsOn) return "hands-on";
  if (id && TASK_PATH_ITEM_IDS.has(id)) return "task";
  if (itemType === "homework") return "homework";
  return "reading";
}
